import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Trash2 } from "lucide-react";

import DashboardLayout from "./DashboardLayout";
import { apiFetch } from "../utils/apiClient";
import { clearAuthUser, getAuthUser, saveAuthUser } from "../utils/auth";

const buildInitialForm = (user) => ({
  name:
    user?.name ||
    user?.fullName ||
    user?.username ||
    (user?.email ? user.email.split("@")[0] : ""),
  email: user?.email || "",
  phone: user?.phone || "",
  location: user?.location || user?.city || "",
});

export default function Profile() {
  const navigate = useNavigate();
  const authUser = useMemo(() => getAuthUser(), []);
  const [storedUser, setStoredUser] = useState(authUser);
  const userIdentifier = useMemo(() => {
    if (storedUser?.id) return storedUser.id;
    if (storedUser?._id) return storedUser._id;
    if (authUser?.id) return authUser.id;
    if (authUser?._id) return authUser._id;
    return null;
  }, [storedUser, authUser]);

  const [form, setForm] = useState(() => buildInitialForm(authUser));
  const [photo, setPhoto] = useState(
    () => authUser?.avatarUrl || authUser?.avatar || authUser?.profileImage || "",
  );
  const [preview, setPreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [savedMessage, setSavedMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      try {
        const response = await apiFetch("/users/profile", {
          ...(userIdentifier
            ? {
                headers: {
                  "x-user-id": userIdentifier,
                },
              }
            : {}),
        });
        if (!isMounted) return;

        if (response?.user) {
          setStoredUser(response.user);
          saveAuthUser(response.user);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage((prev) => prev || "Failed to load profile details.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [userIdentifier]);

  useEffect(() => {
    if (loading) {
      return;
    }

    setForm(buildInitialForm(storedUser));
    setPhoto(storedUser?.avatarUrl || storedUser?.avatar || storedUser?.profileImage || "");
    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview("");
    }
  }, [storedUser, loading,preview]);

  const handleSignOut = () => {
    clearAuthUser();
    navigate("/login", { replace: true });
  };

  const handleInputChange = (field) => (event) => {
    const { value } = event.target;
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleReset = () => {
    setForm(buildInitialForm(storedUser));
    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview("");
    }
    setPhoto(storedUser?.avatarUrl || storedUser?.avatar || storedUser?.profileImage || "");
    setSavedMessage("");
    setErrorMessage("");
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (preview) {
      URL.revokeObjectURL(preview);
    }
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);

    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        setPhoto(reader.result);
      }
    };
    reader.readAsDataURL(file);

    event.target.value = "";
  };

  const handleRemovePhoto = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview("");
    }
    setPhoto("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setSavedMessage("");
    setErrorMessage("");

    const payload = {
      name: form.name?.trim(),
      phone: form.phone?.trim(),
      city: form.location?.trim(),
      location: form.location?.trim(),
      avatarUrl: typeof photo === "string" ? photo : "",
    };

    try {
      const response = await apiFetch("/users/profile", {
        method: "PATCH",
        ...(userIdentifier
          ? {
              headers: {
                "x-user-id": userIdentifier,
              },
            }
          : {}),
        data: payload,
      });

      const nextUser = response?.user
        ? {
            ...storedUser,
            ...response.user,
            location: response.user?.city || storedUser?.location || storedUser?.city || "",
          }
        : { ...storedUser };
      saveAuthUser(nextUser);
      setStoredUser(nextUser);
      setSavedMessage(response?.message || "Profile updated successfully.");
      setTimeout(() => setSavedMessage(""), 2500);
    } catch (error) {
      setErrorMessage(error?.data?.message || error.message || "Failed to update profile.");
    } finally {
      setSubmitting(false);
    }
  };

  const displayPhoto =
    preview ||
    photo ||
    "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=500&auto=format&fit=crop";

  return (
    <DashboardLayout user={storedUser} onSignOut={handleSignOut} activeKey="profile">
      {() => (
        <div className="space-y-10 pb-24 sm:pb-0">
          {loading ? (
            <div className="flex h-72 items-center justify-center text-white/70">
              Loading profile…
            </div>
          ) : (
            <>
              <section className="mx-auto w-full max-w-8xl space-y-10 text-left">
                <header className="flex flex-col gap-4  text-left md:flex-row md:items-center md:justify-between">
                  <div className="flex flex-col gap-2 md:flex-1">
                    <div className="text-sm text-white/50">
                      Dashboard <span className="mx-2 text-white/30">›</span> Profile
                    </div>
                    <div className="flex items-center gap-6">
                      <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
                        Profile
                      </h1>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard")}
                    className="self-start rounded-md border border-white/15 px-4 py-2 mt-6 text-xs font-semibold text-white/70 transition hover:border-white/30 hover:text-white md:self-auto"
                  >
                    Go back to your dashboard
                  </button>
                </header>

                <div className="flex flex-col items-center gap-4">
                  <div className="relative h-44 w-44 overflow-hidden rounded-full border-4 border-[#8C3CDD]/60 shadow-[0_20px_55px_rgba(33,12,70,0.45)]">
                    <img src={displayPhoto} alt="Profile" className="h-full w-full object-cover" />
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2 text-white transition hover:bg-white/10"
                    >
                      <Trash2 size={16} />
                      Remove Photo
                    </button>
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/10 bg-white/10 px-5 py-2 text-white transition hover:bg-[#8C3CDD] hover:text-white">
                      <Camera size={16} />
                      Change Photo
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                      />
                    </label>
                  </div>
                </div>

                <hr className="border-t border-white/10" />

                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="grid gap-2 text-sm text-white/70">
                    <label htmlFor="profile-name" className="font-medium text-white/65">
                      Name
                    </label>
                    <input
                      id="profile-name"
                      type="text"
                      value={form.name}
                      onChange={handleInputChange("name")}
                      placeholder="Input"
                      className="rounded-xl border border-white/15 bg-[#101018] px-4 py-3 text-sm text-white focus:border-[#8C3CDD] focus:outline-none focus:ring-2 focus:ring-[#8C3CDD]/35"
                    />
                  </div>

                  <div className="grid gap-2 text-sm text-white/70">
                    <label htmlFor="profile-email" className="font-medium text-white/65">
                      Email
                    </label>
                    <input
                      id="profile-email"
                      type="email"
                      value={form.email}
                      placeholder="Input"
                      readOnly
                      className="rounded-xl border border-white/15 bg-[#101018] px-4 py-3 text-sm text-white/80"
                    />
                  </div>

                  <div className="grid gap-2 text-sm text-white/70">
                    <label htmlFor="profile-phone" className="font-medium text-white/65">
                      Phone Number
                    </label>
                    <input
                      id="profile-phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleInputChange("phone")}
                      placeholder="Input"
                      className="rounded-xl border border-white/15 bg-[#101018] px-4 py-3 text-sm text-white focus:border-[#8C3CDD] focus:outline-none focus:ring-2 focus:ring-[#8C3CDD]/35"
                    />
                  </div>

                  <div className="grid gap-2 text-sm text-white/70">
                    <label htmlFor="profile-location" className="font-medium text-white/65">
                      State / City
                    </label>
                    <input
                      id="profile-location"
                      type="text"
                      value={form.location}
                      onChange={handleInputChange("location")}
                      placeholder="Input"
                      className="rounded-xl border border-white/15 bg-[#101018] px-4 py-3 text-sm text-white focus:border-[#8C3CDD] focus:outline-none focus:ring-2 focus:ring-[#8C3CDD]/35"
                    />
                  </div>

                  <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleReset}
                      className="rounded-xl border border-white/15 bg-transparent px-6 py-3 text-sm font-semibold text-white/80 transition hover:bg-white/10 hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="rounded-xl bg-[#8C3CDD] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#A367FF] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {submitting ? "Saving..." : "Save Edits"}
                    </button>
                  </div>

                  {errorMessage && (
                    <p className="text-sm font-medium text-red-300">{errorMessage}</p>
                  )}
                  {savedMessage && (
                    <p className="text-sm font-medium text-emerald-300">{savedMessage}</p>
                  )}
                </form>

                <div className="rounded-3xl border border-[#3F192C] bg-[#14040F] p-6 text-sm text-white/70 shadow-[0_20px_60px_rgba(45,12,38,0.45)]">
                  <h2 className="text-lg font-semibold text-[#FF3B7F]">Log Out</h2>
                  <p className="mt-2 text-sm text-white/55">
                    Safely log out of SynergyCoin and end your session whenever you&apos;re done.
                  </p>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="mt-4 rounded-xl border border-[#FF3B7F]/60 bg-[#FF3B7F]/15 px-6 py-3 text-sm font-semibold text-[#FF86B0] transition hover:bg-[#FF3B7F]/25"
                  >
                    Log Out
                  </button>
                </div>
              </section>
            </>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
